import { ReactNode, cloneElement, isValidElement, useState } from 'react';

import Context, { ISoftphoneModalOptionsProps } from './Context';
import SoftphoneModal from './components/SoftphoneModal';

interface ISoftphoneModalContextProps {
  children: ReactNode;
  isReversedLayout: boolean;
}

function SoftphoneModalProvider({ children, isReversedLayout }: ISoftphoneModalContextProps) {
  const [modalOptions, setModalOptions] = useState<ISoftphoneModalOptionsProps | undefined>();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = (options?: ISoftphoneModalOptionsProps) => {
    setIsModalOpen(true);
    setModalOptions(options);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    modalOptions?.onClose?.();
  };

  const handleClearContent = () => {
    setModalOptions(undefined);
  };

  const updateModalOptions = (options: Partial<ISoftphoneModalOptionsProps>) => {
    // Help prevent modal updates when a different modal is open
    if (options.id && options.id !== modalOptions?.id) {
      return;
    }

    setModalOptions((existingOptions) => ({ ...(existingOptions || {}), ...options }) as ISoftphoneModalOptionsProps);
  };

  const updateChildProps = (props: any) => {
    setModalOptions((oldState) => {
      if (!oldState) {
        return;
      }
      if (!isValidElement(oldState.content)) {
        return;
      }

      const newChild = cloneElement(oldState.content, props);
      return { ...oldState, newChild };
    });
  };

  return (
    <Context.Provider
      value={{
        state: {
          isModalOpen,
        },
        actions: {
          openModal,
          closeModal,
          updateModalOptions,
          updateChildProps,
        },
      }}
    >
      {children}
      {modalOptions && (
        <SoftphoneModal
          isModalOpen={isModalOpen}
          closeModal={closeModal}
          handleClearContent={handleClearContent}
          isReversedLayout={isReversedLayout}
          {...modalOptions}
        >
          {modalOptions.content}
        </SoftphoneModal>
      )}
    </Context.Provider>
  );
}

export default SoftphoneModalProvider;
